import { useCallback, useEffect, useState } from 'react'
import { digitsOnly, isValidCpf } from '@/utils/cpf'
import { resolveCaptcha } from '@/utils/captcha'
import { normalizeEmail, validateEmail } from '@/utils/authValidation'
import {
  buildSignupProfilePayload,
  normalizePhoneOrThrow,
  validateSignupProfile,
  type SignupProfileInput,
} from '@/utils/signupProfile'
import * as signupFlowMarker from '@/utils/signupFlowMarker'
import * as verificationApi from '@/utils/signupVerificationApi'
import type {
  StartResult,
  VerificationChannel,
} from '@/utils/signupVerificationApi'
import type { CaptchaConfig } from '@/components/AleloCaptcha'
import { toTransactionErrorCode, type TransactionErrorLike } from '@/utils/transactionError'

type SignupSdk = {
  signup: (data: Record<string, string | number | boolean | undefined>) => Promise<void>
  validatePassword?: (password: string) => { valid: boolean; message?: string; errors?: unknown[] }
  validateUsername?: (username: string) => { valid: boolean; errors?: unknown[] }
  getSignupIdentifiers?: () => Array<{ type: string; required: boolean }> | null
  getErrors?: () => TransactionErrorLike[]
  screen?: {
    loginLink?: string | null
    isCaptchaAvailable?: boolean
    captcha?: CaptchaConfig | null
    captchaProvider?: string | null
    captchaSiteKey?: string | null
    captchaImage?: string | null
  }
  transaction?: {
    requiredIdentifiers?: string[] | null
    optionalIdentifiers?: string[] | null
  }
}

export type SignupFormPayload = SignupProfileInput & {
  username: string
  email: string
  password: string
  phone?: string
}

async function createSignupSdk(): Promise<SignupSdk | null> {
  try {
    const mod = await import('@auth0/auth0-acul-js/signup')
    return new mod.default() as unknown as SignupSdk
  } catch {
    return null
  }
}

/**
 * Tela `signup` (fluxo clássico / single-step) — CPF + e-mail + senha (+ phone/nome).
 * @see https://auth0.com/docs/libraries/acul/js-sdk/Screens/classes/Signup
 */
export function useSignupManager() {
  const [error, setError] = useState<string>()
  const [sdk, setSdk] = useState<SignupSdk | null>(null)
  const [captchaToken, setCaptchaToken] = useState<string>()

  useEffect(() => {
    let cancelled = false
    void (async () => {
      const instance = await createSignupSdk()
      if (!cancelled) setSdk(instance)
    })()
    return () => {
      cancelled = true
    }
  }, [])

  const captchaConfig = resolveCaptcha(sdk?.screen ?? null)
  const captchaRequired = Boolean(captchaConfig?.provider)
  const onCaptchaToken = useCallback((token: string | undefined) => {
    setCaptchaToken(token)
  }, [])

  /**
   * Validações que precedem qualquer envio de código: as do formulário e as
   * políticas do tenant. Lança os mesmos códigos de erro que a tela já mapeia.
   */
  async function prepare(form: SignupFormPayload) {
    setError(undefined)
    const username = digitsOnly(form.username)
    if (!isValidCpf(username)) {
      throw new Error('CPF_INVALID')
    }
    validateSignupProfile(form)
    const e164 = normalizePhoneOrThrow(form.phone)
    const emailError = validateEmail(form.email)
    if (emailError) {
      throw new Error(emailError)
    }
    if (!form.password || form.password.length < 8) {
      throw new Error('PASSWORD_WEAK')
    }
    if (captchaRequired && !captchaToken) {
      throw new Error('CAPTCHA_REQUIRED')
    }

    const instance = sdk ?? (await createSignupSdk())

    if (instance?.validateUsername) {
      const u = instance.validateUsername(username)
      if (u && u.valid === false) {
        setError('Não é possível usar este CPF no cadastro.')
        throw new Error('USERNAME_POLICY')
      }
    }
    if (instance?.validatePassword) {
      const p = instance.validatePassword(form.password)
      if (p && p.valid === false) {
        setError('A senha não atende aos requisitos de segurança.')
        throw new Error('PASSWORD_POLICY')
      }
    }

    return { username, e164, instance }
  }

  /**
   * Primeira metade do cadastro: valida e pede ao BFF o código do canal.
   *
   * Nenhuma conta é criada aqui. Sem canal exigido, o próprio BFF devolve o
   * token e a tela segue direto para `completeSignup`.
   */
  async function startVerification(
    form: SignupFormPayload,
    options: { channel?: VerificationChannel; sessionId?: string } = {},
  ): Promise<StartResult> {
    const { username, e164 } = await prepare(form)
    return verificationApi.start({
      cpf: username,
      email: normalizeEmail(form.email),
      phone: e164,
      channel: options.channel,
      sessionId: options.sessionId,
    })
  }

  /**
   * Segunda metade: só aqui `instance.signup()` é chamado, com o token de
   * verificação no payload. A Action `new-user-registration-validation` nega
   * o cadastro se ele faltar ou não conferir.
   */
  async function completeSignup(form: SignupFormPayload, token: string) {
    const { username, e164, instance } = await prepare(form)

    const payload: Record<string, string | undefined> = {
      username,
      email: normalizeEmail(form.email),
      password: form.password,
      'ulp-verification_token': token,
      ...(e164
        ? {
            phoneNumber: e164,
            phone: e164,
            'ulp-phone': e164,
          }
        : {}),
      ...(captchaToken ? { captcha: captchaToken } : {}),
      ...buildSignupProfilePayload(form),
    }

    if (instance?.signup) {
      signupFlowMarker.start()
      try {
        await instance.signup(payload)
      } catch (err) {
        signupFlowMarker.clear()
        throw err
      }
      return
    }

    signupFlowMarker.start()
    console.info('[preview signup]', {
      ...payload,
      password: '***',
      // O token de verificação nunca vai para o log (SBV-08); só o sinal de
      // que ele foi incluído no payload.
      'ulp-verification_token': '***',
    })
    window.dispatchEvent(
      new CustomEvent('alelo:acul-preview', {
        detail: { screen: 'signup', action: 'signup', data: payload },
      }),
    )
  }

  function handleBackToLogin() {
    const link = sdk?.screen?.loginLink
    if (link) {
      window.location.assign(link)
      return
    }
    window.location.search = '?screen=login-id'
  }

  // Falha do cadastro volta no re-render do Auth0; vira código (a tela
  // mostra a copy), nunca o texto do provedor.
  const serverError = toTransactionErrorCode(sdk?.getErrors?.())

  return {
    startVerification,
    completeSignup,
    handleBackToLogin,
    captchaConfig,
    captchaReady: !captchaRequired || Boolean(captchaToken),
    onCaptchaToken,
    error: error || serverError,
  }
}
